import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Shield, Clock, Zap, FileText } from 'lucide-react';
import FreeReportModal from '@/components/shared/FreeReportModal';
import { useState, useEffect } from 'react';
import { useAnalytics } from '@/hooks/useAnalytics';
import { calculateAIRiskScore } from '@/lib/aiRisk';

interface AIRiskPreviewProps {
  occupation: string;
  soc: string;
  area?: string;
  className?: string;
}

const AIRiskPreview: React.FC<AIRiskPreviewProps> = ({
  occupation,
  soc,
  area = 'United States',
  className = ''
}) => {
  const analytics = useAnalytics();
  const [showReportModal, setShowReportModal] = useState(false);

  // Track component view
  useEffect(() => {
    analytics.trackAIAnalysisView('occupation_page_preview', soc);
  }, [analytics, soc]);

  const aiRisk = calculateAIRiskScore(soc, occupation);

  const getRiskColor = (score: number) => {
    if (score < 30) return 'bg-green-500';
    if (score < 50) return 'bg-yellow-500';
    if (score < 75) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getRiskBadgeVariant = (level: string) => {
    if (level === 'Low') return 'default';
    if (level === 'Moderate') return 'secondary';
    return 'destructive';
  };

  return (
    <>
      <Card className={`border-primary/20 bg-gradient-to-br from-primary/5 to-transparent ${className}`}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Zap className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">AI Replacement Risk Analysis</CardTitle>
                <CardDescription>Forward-looking analysis of automation and AI impact</CardDescription>
              </div>
            </div>
            <Badge variant={getRiskBadgeVariant(aiRisk.riskLevel)}>
              {aiRisk.riskLevel} Risk
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Risk Meter */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium">AI Risk Score</span>
              <span className="font-bold">{aiRisk.riskScore}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 relative overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${getRiskColor(aiRisk.riskScore)}`}
                style={{ width: `${aiRisk.riskScore}%` }}
              />
            </div>
          </div>

          {/* Timeline Estimates */}
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="text-center p-2 bg-muted rounded">
              <Clock className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <div className="font-medium text-xs">Near-term</div>
              <div className="text-xs text-muted-foreground">1-3 years</div>
              <Badge variant="outline" className="mt-1 text-xs">
                {aiRisk.timeline.nearTerm}
              </Badge>
            </div>
            <div className="text-center p-2 bg-muted rounded">
              <Clock className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <div className="font-medium text-xs">Medium-term</div>
              <div className="text-xs text-muted-foreground">3-7 years</div>
              <Badge variant="outline" className="mt-1 text-xs">
                {aiRisk.timeline.mediumTerm}
              </Badge>
            </div>
            <div className="text-center p-2 bg-muted rounded">
              <Clock className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <div className="font-medium text-xs">Long-term</div>
              <div className="text-xs text-muted-foreground">7-15 years</div>
              <Badge variant="outline" className="mt-1 text-xs">
                {aiRisk.timeline.longTerm}
              </Badge>
            </div>
          </div>

          {/* Key Factors Preview */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <AlertCircle className="h-4 w-4 text-primary" />
              Key Risk Factors
            </div>
            <ul className="space-y-1 text-xs text-muted-foreground">
              {aiRisk.factors.slice(0, 2).map((factor, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Shield className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                  <span>{factor}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="pt-2 border-t">
            <div className="space-y-2">
              <Button 
                className="w-full"
                onClick={() => {
                  analytics.trackAIAnalysisCTAClick('preview_free_report_cta', soc, false);
                  setShowReportModal(true);
                }}
              >
                <FileText className="h-4 w-4 mr-2" />
                Get Your Free AI Risk Report
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Includes risk scoring, timeline estimates, adaptation strategies, and more
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Free Report Modal */}
      <FreeReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        occupation={occupation}
        area={area}
        soc={soc}
        source="ai_risk_preview"
      />
    </>
  );
};

export default AIRiskPreview;
